import { animate, keyframes, transition, trigger } from '@angular/animations';
import { Clipboard } from '@angular/cdk/clipboard';
import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, ElementRef } from '@angular/core';
import { IProduct2 } from 'situview-webapi-client';
import * as kf from '../../../core/keyframes';
import { AuthService, StateService, Utils } from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ArtifactViewerDialogComponent } from 'src/app/components';

@Component({
  selector: 'thumb-product-card',
  templateUrl: './thumb-product-card.component.html',
  styleUrls: ['./thumb-product-card.component.scss'],
  animations: [
    trigger('cardAnimator', [
      transition('* => wobble', animate(1000, keyframes(kf.wobble))),
      transition('* => swing', animate(1000, keyframes(kf.swing))),
      transition('* => jello', animate(1000, keyframes(kf.jello))),
      transition('* => zoomOutRight', animate(1000, keyframes(kf.zoomOutRight))),
      transition('* => slideOutLeft', animate(1000, keyframes(kf.slideOutLeft))),
      transition('* => rotateOutUpRight', animate(1000, keyframes(kf.rotateOutUpRight))),
      transition('* => flipOutY', animate(1000, keyframes(kf.flipOutY))),
      transition('* => headShake', animate(1000, keyframes(kf.headShake)))
    ])
  ]
})
export class ThumbProductCardComponent extends SituBaseComponent implements OnInit, OnChanges {
  @Input() item: IProduct2 | undefined;
  @Input() selectedCompanyId: string = '';
  @Input() isCatalog: boolean = true;
  @Input() canExpandImage: boolean = false;
  @Input() editProductOnClick: boolean = false;
  @Input() searchTerm: string = "";
  @Input() selected: boolean = false;
  @Input() animationState: string = '';
  @Input() situView: string = '';
  @Input() companyName: string = '';
  @Input() companyLogo: string = '';
  @Input() productTypeColor: string = '';
  @Input() productTypeName: string = '';

  @Output() filterCompanyId: EventEmitter<string> = new EventEmitter();
  @Output() filterProductTypeId: EventEmitter<string> = new EventEmitter();
  @Output() createPreview: EventEmitter<string> = new EventEmitter();
  @Output() shareSituLink: EventEmitter<string> = new EventEmitter();
  @Output() productDownloadRequest: EventEmitter<ProductDownloadRequest> = new EventEmitter();

  showDownloadOptions: boolean = false;

  isImageExpanded: boolean = false;
  isImageShrinking: boolean = false;
  imageHoverTimeout: any;

  imageName = 'thumbnail';
  imageX = 0;
  imageY = 0;
  nextImageToPreload?: string;

  isAuthorizedForProductsCompany: boolean = false;

  expandedCardTranslationX = 0;

  dialogRef: MatDialogRef<ArtifactViewerDialogComponent> | undefined;

  get themeUsageTooltip(): string {
    if (!this.item?.themeUsages?.length) {
      return `Used in ${this.item?.themeUsageCount ?? 0} themes`;
    }
    const grouped = new Map<string, number>();
    for (const usage of this.item.themeUsages) {
      const name = usage.companyName || 'Unknown';
      grouped.set(name, (grouped.get(name) || 0) + 1);
    }
    const lines = Array.from(grouped.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([company, count]) => `${company}: ${count}`);
    return `Themes (${this.item.themeUsageCount})\n${lines.join('\n')}`;
  }

  get noPreviewImageIcon(): string {
    var productType = this.productTypeName.toLowerCase();
    switch (productType) {
      case 'window':
        return 'window-icon.svg';
      case 'mirror':
        return 'mirror-icon.svg';
      case 'shower screen':
        return 'shower-icon.svg';
      case 'stairs':
        return 'stairs-icon.svg';
      default:
        return 'no-image-icon.svg';
    }
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private elem: ElementRef<HTMLElement>,
    private clipboard: Clipboard,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {
    super(authService, stateService);
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  protected onUserChange(): void {
    this.isAuthorizedForProductsCompany = !!this.item && this.authService.isAuthorizedForCompanyById(this.item.companyId!);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.animationState) {
      this.startAnimation(changes.animationState.currentValue);
    }
  }

  ngAfterViewInit() {
    // Calculate the translation based on image and container size
    //this.calculateTranslation();
  }

  startAnimation(state: string) {
    if (!this.animationState) {
      this.animationState = state;
    }
  }

  resetAnimationState() {
    this.animationState = '';
  }

  selectCompanyByProductCompanyId(companyId: string) {
    this.filterCompanyId.emit(companyId);
  }

  selectProductTypeByProduct(productTypeId: string) {
    this.filterProductTypeId.emit(productTypeId);
  }

  copyManufacturerProductDetails(item: IProduct2) {
    if (item) {
      this.clipboard.copy(`Manufacturer: "${this.companyName}" / SKU: "${this.getKeySku(item)}"`);
        this.snackBar.open(`Copied product details to clipboard.`, "Close", {
          duration: 5000,
          panelClass: ['copied-snackbar']
        });
    }
  }

  getKeySku(item: IProduct2) {
    if (item) {
      return item.callerReferenceKey?.split("\\").reverse()[0];
    }
  }

  getPreview(callerReferenceKey: string) {
    this.createPreview.emit(callerReferenceKey);
  }

  share(artifactId: string) {
    this.shareSituLink.emit(artifactId);
  }

  downloadFile(file: string) {
    if (this.item) {
      const extension = Utils.getFileExtension(file);
      this.productDownloadRequest.emit(new ProductDownloadRequest(this.item!.previewArtifactId!, file, `${this.item.name}${this.item.sku}.${extension}`))
    }
  }

  onView3dPreview(product: IProduct2) {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    const dialogWidth = `${Math.round((70 / 100) * screenWidth)}px`;
    const dialogHeight = `${Math.round((90 / 100) * screenHeight)}px`;

    if (product?.previewArtifactId) {
      this.dialogRef = this.dialog.open(ArtifactViewerDialogComponent, {
        width: dialogWidth,
        height: dialogHeight,
        data: {
          fileName: "product.glb",
          displayName: product.name,
          artifactId: product.previewArtifactId,
          additionalFileNames: ArtifactViewerDialogComponent.productPreviewFiles,
        }
      });
    }
  }

  userCanEditProduct() : boolean {
    if (!this.isCatalog) {
      return false; // Editing a product is only accessible in catalog mode
    }

    if (this.isSituUser) {
      return true; // Situ admin etc can access this page
    }

    if (this.isExternalProductContractorUser) {
      return true; // This user can always access the page
    }

    
    if (this.isAuthorizedForProductsCompany &&
      (this.isAdmin || this.isCompanyProductPublisherUser)
    ) {
      return true; // These users can access the tab if the product is from a company they are authorised to access
    }

    return false;
  }

  onMouseEnterImage() {
    if (!this.canExpandImage || !this.item?.previewArtifactId) {
      return;
    }

    this.imageHoverTimeout = setTimeout(() => {
      this.isImageExpanded = true; // Expand the image after 1 second

      //this.expandedCardTranslationX = this.calculateTranslation();
    }, 1000); // 1000 ms = 1 second
  }

  onMouseLeaveImage() {
    if (!this.canExpandImage || !this.item?.previewArtifactId) {
      return;
    }

    clearTimeout(this.imageHoverTimeout); // Clear the timer if the mouse leaves before 1 second
    this.imageHoverTimeout = undefined;

    setTimeout(() => {
      if (!this.imageHoverTimeout) {
        this.isImageExpanded = false; // Reset the image back to normal size
        this.isImageShrinking = true; // Set the image to shrinking so the z-index can be managed
        this.expandedCardTranslationX = 0;

        setTimeout(() => {
          this.isImageShrinking = false;
        }, 200);
      }
    }, 200); // 1000 ms = 1 second

  }

  onMouseMoveImage(event: MouseEvent) {
    // Get the dimensions of the image
    const imgElement = event.target as HTMLElement;
    const rect = imgElement.getBoundingClientRect();
    const x = event.clientX - rect.left; // Mouse X relative to the image
    const y = event.clientY - rect.top;  // Mouse Y relative to the image

    // Calculate which cell the mouse is over
    const column = Math.min(Math.floor(x / (rect.width / 3)), 2); // Divide width into 3 columns (0 - 2)
    const row = Math.min(Math.floor(y / (rect.height / 3)), 2);   // Divide height into 3 rows (0 - 2)

    // Set the hover image based on the calculated cell
    this.imageName = `thumbnail_${row}_${column}`;
    
    // Calculate the image translation based on mouse position
    const translateFactor = -5; // Factor to control the amount of translation
    this.imageX = ((x / rect.width) - 0.5) * translateFactor;
    this.imageY = ((y / rect.height) - 0.5) * translateFactor;
  }

  calculateTranslation() : number {
    if (!this.canExpandImage || !this.item?.previewArtifactId) {
      return 0;
    }

    // NOTE: Add product-expanded-image-container to the parent container if you want to shift the expanded image to always be inside of the parent container
    const imageContainerElement = document.getElementsByClassName('product-expanded-image-container').item(0) as HTMLElement;

    if (!imageContainerElement) {
      return 0;
    }

    const productCardElement = this.elem.nativeElement;

    const imageRect = productCardElement.getBoundingClientRect();
    const wrapperRect = imageContainerElement.getBoundingClientRect();

    const imageCenterX = imageRect.x + (imageRect.width * 0.5);
    // Image is scaled to 3 times its original size
    const scaleFactor = 3;

    const scaledImageLeft = imageCenterX - (imageRect.width * scaleFactor * 0.5);
    if (scaledImageLeft < wrapperRect.left) {
      return (wrapperRect.left - scaledImageLeft) * 0.5;
    }

    const scaledImageRight = imageCenterX + (imageRect.width * scaleFactor * 0.5);
    if (scaledImageRight > wrapperRect.right) {
      return (wrapperRect.right - scaledImageRight) * 0.5;
    }

    return 0;
  }
}

export class ProductDownloadRequest {
  constructor(public artifactId: string, public file: string, public fileName: string) {}
}
